<script lang="ts">
	import type { TooltipOptions } from './types';
	import { arrow, computePosition, flip, offset, shift } from '@floating-ui/dom';
	import { createEventDispatcher, onMount } from 'svelte';

	export let trigger: HTMLElement;
	export let options: TooltipOptions;
	const dispatch = createEventDispatcher<{ hidden: void }>();
	let visible = false;
	let el: HTMLElement;
	let arrowEl: HTMLElement;

	function setPos(el: HTMLElement, { x, y }: { x?: number; y?: number }) {
		Object.assign(el.style, {
			left: x != null ? `${x}px` : '',
			top: y != null ? `${y}px` : '',
		});
	}

	// called by the tooltip action to trigger fade out
	export function __hide() {
		visible = false;
	}

	function transitionend() {
		if (!visible) {
			// listened on by tooltip action to destroy the component after
			dispatch('hidden');
		}
	}

	onMount(() => {
		computePosition(trigger, el, {
			placement: options.placement ?? 'top',
			middleware: [
				offset(10),
				flip({
					fallbackAxisSideDirection: 'start',
					crossAxis: false,
				}),
				shift({ padding: 5 }),
				arrow({
					element: arrowEl,
					padding: 5,
				}),
			],
		}).then(({ x, y, middlewareData, placement }) => {
			setPos(el, { x, y });
			if (middlewareData.arrow) {
				const { x: arrowX, y: arrowY } = middlewareData.arrow;
				setPos(arrowEl, { x: arrowX, y: arrowY });
			}
			const side: string = placement.split('-')[0];

			const staticSide: string
				= {
					top: 'bottom',
					right: 'left',
					bottom: 'top',
					left: 'right',
				}[side] || 'bottom';

			if (middlewareData.arrow) {
				Object.assign(arrowEl.style, {
					[staticSide]: `${-arrowEl.offsetWidth / 2}px`,
				});
			}
			// everything is in place, start in transition
			visible = true;
		});
	});
</script>

<div
	class="tooltip absolute rd-2 py-2 px-3 bg-coloredneutral text-sm text-body text-white shadow-float z-float opacity-0 transition-opacity"
	class:opacity-100={visible}
	bind:this={el}
	on:transitionend={transitionend}
	role="tooltip"
>
	{#if options.header}
		<span class="font-bold">{options.header}</span><br />
	{/if}
	{options.text}
	<div
		bind:this={arrowEl}
		class="arrow absolute bg-coloredneutral rotate-45 w-2 h-2 pointer-events-none shadow-float z-behind"
	/>
</div>

<style>
	.tooltip {
		width: max-content;
		max-width: 200px;
	}
</style>
